import type { Attempt } from '@ranked-waiting/game-core';
import { formatSignedOffsetMs } from './format';

/**
 * Tier boundaries as a percentage of the round's own target duration (owner
 * decision, 2026-08-14; bands roughly doubled + a gold bullseye tier and a
 * beyond-the-scale way-off tier added 2026-08-18) — a fixed ms cutoff meant
 * very different things on a 3s target vs. a 60s one, so tiers scale with the
 * target itself. The 2026-08-14 bands (1/3/5%) were too punishing — a 2%
 * miss reading as "orange/off" felt harsh — so the owner widened them and
 * added a top gold tier for a near-perfect hit:
 *   gold  ≤ 0.25%   green ≤ 1%   yellow ≤ 2.5%   orange ≤ 5%   red ≤ 8%
 *   way-off > 8%  (a miss past the whole tolerance scale)
 */
const GOLD_THRESHOLD_PCT = 0.25;
const GREEN_THRESHOLD_PCT = 1;
const YELLOW_THRESHOLD_PCT = 2.5;
const ORANGE_THRESHOLD_PCT = 5;
const RED_THRESHOLD_PCT = 8;

export type ResultTier = 'gold' | 'green' | 'yellow' | 'orange' | 'red' | 'way-off' | 'false-start';

/** How far an attempt landed from its target, as a percentage of that target. */
export function errorPercent(attempt: Attempt): number {
  if (!Number.isFinite(attempt.targetMs) || attempt.targetMs <= 0) return 0;
  return (Math.abs(attempt.elapsedMs - attempt.targetMs) / attempt.targetMs) * 100;
}

export function resultTier(attempt: Attempt): ResultTier {
  if (attempt.isFalseStart) return 'false-start';
  const pct = errorPercent(attempt);
  if (pct <= GOLD_THRESHOLD_PCT) return 'gold';
  if (pct <= GREEN_THRESHOLD_PCT) return 'green';
  if (pct <= YELLOW_THRESHOLD_PCT) return 'yellow';
  if (pct <= ORANGE_THRESHOLD_PCT) return 'orange';
  if (pct <= RED_THRESHOLD_PCT) return 'red';
  return 'way-off';
}

/** A short, punchy word for each tier — the headline on the accuracy reveal. */
export function tierLabel(tier: ResultTier): string {
  switch (tier) {
    case 'gold':
      return 'BULLSEYE';
    case 'green':
      return 'BANG ON';
    case 'yellow':
      return 'CLOSE';
    case 'orange':
      return 'OFF';
    case 'red':
      return 'WAY OFF';
    case 'way-off':
      return 'MILES OFF';
    case 'false-start':
      return 'FALSE START';
  }
}

/**
 * A small glowing chip showing a signed offset ("+0.07s") coloured by how
 * close it was — the reveal-effect badge used across Practice, the 10s
 * Challenge, 1v1 duels and Knockout rounds (owner decision, 2026-08-11: "one
 * shared vibe" across every mode's result reveal). `highlight` marks a
 * session/round best with an extra sparkle treatment.
 */
export function resultBadgeMarkup(attempt: Attempt, options: { highlight?: boolean } = {}): string {
  const tier = resultTier(attempt);
  const label = tier === 'false-start' ? 'FALSE START' : formatSignedOffsetMs(attempt.elapsedMs, attempt.targetMs);
  const highlightClass = options.highlight && tier !== 'false-start' ? ' result-badge--highlight' : '';
  return `<span class="result-badge result-badge--${tier}${highlightClass}">${label}</span>`;
}
